<script setup lang="ts">
  import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import {
    CasesService,
    type CustomerCase,
    EntityTypeEnum,
    OpenAPI,
    ActivitiesService,
    type TActivity
  } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'
  import SpaCompatComponent from '@/components/app/SpaCompatComponent.vue'
  import {
    ClipboardPlus,
    ClipboardList,
    ClipboardCheck,
    ClipboardX,
    Plus,
    MoreHorizontal,
    Eye,
    EyeOff,
    FileText,
    File,
    Hourglass,
    Search,
    Loader2,
    ChevronDown,
    Check,
    X,
    Trash2
  } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { Input } from '@/components/ui/input'
  import { Textarea } from '@/components/ui/textarea'
  import { TableCell, TableRow } from '@/components/ui/table'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@/components/ui/alert-dialog'
  import { PopoverClose } from 'radix-vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import PaginatedTableView from '@/components/admin/PaginatedTable.vue'
  import SortableTableHeader from '@/components/admin/SortableTableHeader.vue'
  import type { PaginatedResponse, FetchDataOptions } from '@/components/admin/PaginatedTable.vue'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'

  const { t } = useTranslation({
    en: {
      // Page header
      requests: 'Requests',
      manageClientRequests: 'Manage your client requests',
      createNewRequest: 'Create new request',

      // State labels
      newRequests: 'New Requests',
      inProgress: 'In Progress',
      completed: 'Completed',
      rejected: 'Rejected',

      // Filter options
      legalArea: 'Legal Area',
      clerkLabel: 'Clerk',
      all: 'All',
      allLawFields: 'All law fields',
      allClerks: 'All clerks',
      notAssigned: 'Not assigned',
      search: 'Search',

      // Search & filters
      searchPlaceholder: 'Search for name, notes...',
      onlyUnseenActivity: 'Only with unseen activity',
      requestsHiddenByFilter: 'requests hidden by filter',
      resetFilters: 'Reset filters',
      clearAll: 'Clear all',

      // Table headers
      received: 'Received',
      lastActivity: 'Last Activity',
      name: 'Name',
      lawField: 'Law field',
      lawTopic: 'Topic',
      clerk: 'Clerk',
      documents: 'Documents',
      nDocuments: '{n} documents | {n} document | {n} documents',
      nRequestedForms: '{n} requested forms | {n} requested form | {n} requested forms',
      viewAllDocuments: 'View all documents',
      note: 'Note',

      // Clerk assignment
      loading: 'Loading...',
      assignClerk: 'Assign clerk',
      addClerk: '+ Add a clerk',

      // Note
      addNote: '+ Add a note',
      editNote: 'Edit note',
      enterNote: 'Enter note...',

      // Actions
      markAsRead: 'Mark as read',
      markAsUnread: 'Mark as unread',
      deleteCase: 'Delete case',
      deleteConfirmTitle: 'Are you sure?',
      deleteConfirmDescription:
        'This action cannot be undone. The request and all associated data will be permanently deleted.',
      deleteConfirmCancel: 'Cancel',
      deleteConfirmDelete: 'Delete',

      // Activity popup
      latestActivity: 'Latest activity',
      loadingActivities: 'Loading activities...',
      noActivitiesFound: 'No activities found',
      activityCaseCreated: 'Request created',
      activityDocumentSigned: 'Document signed',
      activityDocumentSignedWithTitle: 'Document "{title}" signed',
      activityQuestionnaireClosed: 'Questionnaire completed',
      activityQuestionnaireClosedWithTitle: 'Questionnaire "{title}" completed',
      activityEmailReceived: 'Email received',
      activityEmailReceivedFrom: 'Email received from {from}',
      activityEmailSent: 'Email sent',
      activityAppointmentCreated: 'Appointment created',
      systemUser: 'JUPUS system',

      // Empty state
      noRequestsFound: 'No requests found',
      noRequestsYet: "You don't have any requests yet.",
      noResultsWithFilters: 'The current filters return no results for this request status.',
      noRequestsInStatus: 'No requests in "{status}".',
      requestsInOtherStatus: 'Requests in other status:',

      // Dialog
      cancel: 'Cancel',
      save: 'Save',

      // Feature announcement
      announcementTitle: 'New inquiry overview with improved design',
      announcementDescription:
        'The new design and additional features make the inquiry list clearer and easier to use. Notes, clerks, and filters help you handle relevant cases faster.',
      announcementDone: 'Done'
    },
    de: {
      // Page header
      requests: 'Anfragen',
      manageClientRequests: 'Verwalten Sie Ihre Mandantenanfragen',
      createNewRequest: 'Neue Anfrage anlegen',

      // State labels
      newRequests: 'Neue Anfragen',
      inProgress: 'In Bearbeitung',
      completed: 'Abgeschlossen',
      rejected: 'Abgelehnt',

      // Filter options
      legalArea: 'Rechtsgebiet',
      clerkLabel: 'Sachbearbeiter',
      all: 'Alle',
      allLawFields: 'Alle Rechtsgebiete',
      allClerks: 'Alle Sachbearbeiter',
      notAssigned: 'Nicht zugewiesen',
      search: 'Suchen',

      // Search & filters
      searchPlaceholder: 'Nach Name, Notizen suchen...',
      onlyUnseenActivity: 'Nur mit ungesehener Aktivität',
      requestsHiddenByFilter: 'Anfragen durch Filter ausgeblendet',
      resetFilters: 'Filter zurücksetzen',
      clearAll: 'Alle löschen',

      // Table headers
      received: 'Eingegangen',
      lastActivity: 'Letzte Aktivität',
      name: 'Name',
      lawField: 'Rechtsgebiet',
      lawTopic: 'Thema',
      clerk: 'Sachbearbeiter',
      documents: 'Dokumente',
      nDocuments: '{n} Dokumente | {n} Dokument | {n} Dokumente',
      nRequestedForms:
        '{n} angeforderte Formulare | {n} angefordertes Formular | {n} angeforderte Formulare',
      viewAllDocuments: 'Alle Dokumente ansehen',
      note: 'Notiz',

      // Clerk assignment
      loading: 'Laden...',
      assignClerk: 'Sachbearbeiter zuweisen',
      addClerk: '+ Sachbearbeiter',

      // Note
      addNote: '+ Notiz',
      editNote: 'Notiz bearbeiten',
      enterNote: 'Notiz eingeben...',

      // Actions
      markAsRead: 'Als gelesen markieren',
      markAsUnread: 'Als ungelesen markieren',
      deleteCase: 'Fall löschen',
      deleteConfirmTitle: 'Sind Sie sicher?',
      deleteConfirmDescription:
        'Diese Aktion kann nicht rückgängig gemacht werden. Die Anfrage und alle zugehörigen Daten werden dauerhaft gelöscht.',
      deleteConfirmCancel: 'Abbrechen',
      deleteConfirmDelete: 'Löschen',

      // Activity popup
      latestActivity: 'Letzte Aktivität',
      loadingActivities: 'Aktivitäten werden geladen...',
      noActivitiesFound: 'Keine Aktivitäten gefunden',
      activityCaseCreated: 'Anfrage erstellt',
      activityDocumentSigned: 'Dokument unterschrieben',
      activityDocumentSignedWithTitle: 'Dokument "{title}" unterschrieben',
      activityQuestionnaireClosed: 'Fragebogen ausgefüllt',
      activityQuestionnaireClosedWithTitle: 'Fragebogen "{title}" ausgefüllt',
      activityEmailReceived: 'E-Mail erhalten',
      activityEmailReceivedFrom: 'E-Mail erhalten von {from}',
      activityEmailSent: 'E-Mail gesendet',
      activityAppointmentCreated: 'Termin erstellt',
      systemUser: 'JUPUS system',

      // Empty state
      noRequestsFound: 'Keine Anfragen gefunden',
      noRequestsYet: 'Sie haben noch keine Anfragen.',
      noResultsWithFilters:
        'Die aktuellen Filter ergeben keine Treffer zu diesem Status der Anfrage.',
      noRequestsInStatus: 'In "{status}" sind keine Anfragen vorhanden.',
      requestsInOtherStatus: 'Anfragen in anderem Status der Anfrage:',

      // Dialog
      cancel: 'Abbrechen',
      save: 'Speichern',

      // Feature announcement
      announcementTitle: 'Neue Anfrageübersicht mit verbessertem Design',
      announcementDescription:
        'Das neue Design und zusätzliche Funktionen machen die Anfrageliste übersichtlicher und einfacher in der Nutzung. Notizen, Sachbearbeiter und Filter helfen Ihnen, relevante Fälle schneller zu bearbeiten.',
      announcementDone: 'Fertig'
    }
  })

  // Type for the state counts response (until types are regenerated)
  interface StateCount {
    count: number
    has_unseen_activity: boolean
  }

  interface StateCountsResponse {
    total: {
      NEW: StateCount
      IN_PROGRESS: StateCount
      COMPLETED: StateCount
      REJECTED: StateCount
    }
    filtered: {
      NEW: StateCount
      IN_PROGRESS: StateCount
      COMPLETED: StateCount
      REJECTED: StateCount
    } | null
    has_filters: boolean
  }

  // State values matching Django Case.States
  const STATES = {
    NEW: 'NEW',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED',
    REJECTED: 'REJECTED'
  } as const

  type StateType = (typeof STATES)[keyof typeof STATES]

  // Feature announcement configuration
  // Set to false to completely disable the announcement (e.g., after the feature is no longer new)
  const FEATURE_ANNOUNCEMENT_ENABLED = true
  const FEATURE_ANNOUNCEMENT_STORAGE_KEY = 'jupus_case_list_announcement_dismissed_v1'

  // Feature announcement state
  const showFeatureAnnouncement = ref(false)

  const initFeatureAnnouncement = () => {
    if (!FEATURE_ANNOUNCEMENT_ENABLED) return

    const dismissed = localStorage.getItem(FEATURE_ANNOUNCEMENT_STORAGE_KEY)
    if (!dismissed) {
      showFeatureAnnouncement.value = true
    }
  }

  const dismissFeatureAnnouncement = () => {
    showFeatureAnnouncement.value = false
    localStorage.setItem(FEATURE_ANNOUNCEMENT_STORAGE_KEY, 'true')
  }

  // State labels for display
  const stateLabels = computed<Record<StateType, string>>(() => ({
    NEW: t('newRequests'),
    IN_PROGRESS: t('inProgress'),
    COMPLETED: t('completed'),
    REJECTED: t('rejected')
  }))

  // Handle sort from SortableTableHeader
  const handleSort = (field: string, direction: 'asc' | 'desc') => {
    sortBy.value = direction === 'desc' ? `-${field}` : field
    trackEvent(MetricEventType.CASE_LIST_SORT_CHANGED, {
      sort_field: field,
      sort_direction: direction
    })
  }

  const route = useRoute()
  const router = useRouter()

  // Helper to get valid state from URL or default
  const getInitialState = (): StateType => {
    const urlState = route.query.state as string
    if (urlState && Object.values(STATES).includes(urlState as StateType)) {
      return urlState as StateType
    }
    return STATES.NEW
  }

  // Filter states - initialize from URL params
  const selectedState = ref<StateType>(getInitialState())
  const clerkFilter = ref<string>((route.query.clerk as string) || '')
  const lawFieldFilter = ref<string>((route.query.lawField as string) || '')
  const searchQuery = ref<string>((route.query.search as string) || '')
  const onlyUnseenActivity = ref<boolean>(route.query.unseen === 'true')
  const sortBy = ref<string>((route.query.sort as string) || '-created_at')
  const tableRef = ref()

  // Dropdown filter search states
  const lawFieldDropdownOpen = ref(false)
  const lawFieldDropdownSearch = ref('')
  const clerkDropdownOpen = ref(false)
  const clerkDropdownSearch = ref('')

  // Inline clerk assignment search (for table row popovers)
  const inlineClerkSearch = ref('')
  const filteredInlineClerkOptions = computed(() => {
    const search = inlineClerkSearch.value.toLowerCase()
    const options = clerkEditOptions.value.filter((o) => o.value) // exclude "Not assigned" option
    if (!search) return options
    return options.filter((opt) => opt.title.toLowerCase().includes(search))
  })

  // Filtered options for dropdowns
  const filteredLawFieldOptions = computed(() => {
    const search = lawFieldDropdownSearch.value.toLowerCase()
    if (!search) return lawFieldOptions.value
    return lawFieldOptions.value.filter((opt) => opt.title.toLowerCase().includes(search))
  })

  const filteredClerkOptions = computed(() => {
    const search = clerkDropdownSearch.value.toLowerCase()
    if (!search) return clerkOptions.value
    return clerkOptions.value.filter((opt) => opt.title.toLowerCase().includes(search))
  })

  // Get display label for selected filter value
  const selectedLawFieldLabel = computed(() => {
    const option = lawFieldOptions.value.find((opt) => opt.value === lawFieldFilter.value)
    return option?.title || t('allLawFields')
  })

  const selectedClerkLabel = computed(() => {
    const option = clerkOptions.value.find((opt) => opt.value === clerkFilter.value)
    return option?.title || t('allClerks')
  })

  // Update URL when filters change (using history API directly to avoid focus loss)
  const updateUrlParams = () => {
    const params = new URLSearchParams()

    // Always include state (even if default, for clarity)
    params.set('state', selectedState.value)

    if (clerkFilter.value) params.set('clerk', clerkFilter.value)
    if (lawFieldFilter.value) params.set('lawField', lawFieldFilter.value)
    if (searchQuery.value) params.set('search', searchQuery.value)
    if (onlyUnseenActivity.value) params.set('unseen', 'true')
    if (sortBy.value && sortBy.value !== '-created_at') params.set('sort', sortBy.value)

    const newUrl = `${window.location.pathname}?${params.toString()}`
    window.history.replaceState({}, '', newUrl)
  }

  // Note dialog state (for editing)
  const noteDialogOpen = ref(false)
  const editingNoteForCase = ref<string | null>(null)
  const editNoteText = ref('')
  const originalNoteText = ref('')
  const savingNote = ref(false)

  // Delete dialog state
  const showDeleteModal = ref(false)
  const caseToDelete = ref<CustomerCase | null>(null)

  // Clerk update loading state
  const updatingClerkForCase = ref<string | null>(null)

  // Activity popover state
  const activitiesCache = ref<Record<string, TActivity[]>>({})
  const loadingActivities = ref<Record<string, boolean>>({})
  const activityPopoverOpen = ref<Record<string, boolean>>({})
  const activityPopoverCloseTimeout = ref<Record<string, number>>({})

  // User store for accessing organisation users
  const userStore = useUserStore()

  // Law field options from user's organization (only activated law fields)
  const lawFieldOptions = computed(() => {
    const rawLawFields = userStore.user?.org?.law_fields
    const lawFields = Array.isArray(rawLawFields) ? rawLawFields : []
    return [
      { value: '', title: t('allLawFields') },
      ...lawFields.map((field: string) => ({ value: field, title: field }))
    ]
  })

  // Metrics tracking
  const { trackEvent } = useMetrics()

  // Clerk options from organisation users (for filter)
  const clerkOptions = computed(() => {
    if (!userStore.user?.org?.users) return [{ value: '', title: t('allClerks') }]
    const sortedUsers = [...userStore.user.org.users]
      .map((user) => ({
        value: user.uuid,
        title: `${user.first_name} ${user.last_name}`
      }))
      .sort((a, b) => a.title.localeCompare(b.title, 'de'))
    return [{ value: '', title: t('allClerks') }, ...sortedUsers]
  })

  // Clerk options for inline editing (includes "Not assigned" option)
  const clerkEditOptions = computed(() => {
    if (!userStore.user?.org?.users) return [{ value: '', title: t('notAssigned') }]
    const sortedUsers = [...userStore.user.org.users]
      .map((user) => ({
        value: user.uuid,
        title: `${user.first_name} ${user.last_name}`
      }))
      .sort((a, b) => a.title.localeCompare(b.title, 'de'))
    return [{ value: '', title: t('notAssigned') }, ...sortedUsers]
  })

  // Counts for each state (filtered counts when filters active)
  const newCasesCount = ref(0)
  const inProgressCasesCount = ref(0)
  const completedCasesCount = ref(0)
  const rejectedCasesCount = ref(0)

  // Total counts (always unfiltered)
  const totalNewCasesCount = ref(0)
  const totalInProgressCasesCount = ref(0)
  const totalCompletedCasesCount = ref(0)
  const totalRejectedCasesCount = ref(0)

  // Whether filters are currently active
  const hasActiveFilters = ref(false)

  const isCountsLoading = ref(true)
  const hasUnseenActivityByState = ref({
    NEW: false,
    IN_PROGRESS: false,
    COMPLETED: false,
    REJECTED: false
  })

  // Computed: check if there are no requests at all (across all states)
  const hasNoRequestsAtAll = computed(() => {
    return (
      totalNewCasesCount.value === 0 &&
      totalInProgressCasesCount.value === 0 &&
      totalCompletedCasesCount.value === 0 &&
      totalRejectedCasesCount.value === 0
    )
  })

  // Computed: other states that have results (for empty state message)
  const otherStatesWithResults = computed(() => {
    const allStates: StateType[] = [
      STATES.NEW,
      STATES.IN_PROGRESS,
      STATES.COMPLETED,
      STATES.REJECTED
    ]
    const counts: Record<StateType, number> = {
      NEW: newCasesCount.value,
      IN_PROGRESS: inProgressCasesCount.value,
      COMPLETED: completedCasesCount.value,
      REJECTED: rejectedCasesCount.value
    }

    return allStates
      .filter((state) => state !== selectedState.value && counts[state] > 0)
      .map((state) => ({
        state,
        label: stateLabels.value[state],
        count: counts[state]
      }))
  })

  // Watch for filter changes and refresh the table + counts + update URL
  watch([clerkFilter, lawFieldFilter, onlyUnseenActivity], () => {
    updateUrlParams()
    if (tableRef.value) {
      tableRef.value.resetAndRefresh()
    }
    // Refresh counts when filters change
    fetchCounts()
  })

  // Track unseen activity filter separately
  watch(onlyUnseenActivity, (newValue) => {
    if (newValue) {
      trackEvent(MetricEventType.CASE_LIST_UNSEEN_ACTIVITY_FILTER_USED, {
        current_state: selectedState.value
      })
    }
  })

  // Watch for sort changes (only refresh table, not counts)
  watch(sortBy, () => {
    updateUrlParams()
    if (tableRef.value) {
      tableRef.value.refreshData()
    }
  })

  // Debounced search
  let searchTimeout: ReturnType<typeof setTimeout> | null = null
  let searchCountsTimeout: ReturnType<typeof setTimeout> | null = null
  watch(searchQuery, () => {
    updateUrlParams()
    if (searchTimeout) clearTimeout(searchTimeout)
    if (searchCountsTimeout) clearTimeout(searchCountsTimeout)
    searchTimeout = setTimeout(() => {
      if (tableRef.value) {
        tableRef.value.resetAndRefresh()
      }
    }, 300)
    // Also refresh counts with debounce
    searchCountsTimeout = setTimeout(() => {
      fetchCounts()
    }, 300)
  })

  const fetchCases = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<CustomerCase>> => {
    const isSearching = !!searchQuery.value?.trim()
    const response = await CasesService.casesList({
      limit: options?.limit,
      offset: options?.offset,
      state: selectedState.value,
      clerk: clerkFilter.value || undefined,
      legalField: lawFieldFilter.value || undefined,
      search: searchQuery.value || undefined,
      hasUnseenActivity: onlyUnseenActivity.value || undefined,
      ordering: sortBy.value
    })

    // Prefetch first activity for each case to show description
    const paginatedResponse = response as unknown as PaginatedResponse<CustomerCase>
    if (paginatedResponse.results) {
      paginatedResponse.results.forEach((caseItem) => {
        if (caseItem.uuid && caseItem.last_activity_at && !activitiesCache.value[caseItem.uuid]) {
          fetchActivities(caseItem.uuid)
        }
      })
    }

    // Track search results (only for first page of results when searching)
    if (isSearching && options?.offset === 0) {
      trackEvent(MetricEventType.CASE_LIST_SEARCH_PERFORMED, {
        query_length: searchQuery.value!.trim().length,
        current_state: selectedState.value,
        result_count: paginatedResponse.count,
        has_results: paginatedResponse.count > 0
      })
    }

    return paginatedResponse
  }

  const fetchStateCounts = async (): Promise<StateCountsResponse> => {
    // Build query params for filters
    const params = new URLSearchParams()
    if (clerkFilter.value) params.set('clerk', clerkFilter.value)
    if (lawFieldFilter.value) params.set('legal_field', lawFieldFilter.value)
    if (searchQuery.value) params.set('search', searchQuery.value)
    if (onlyUnseenActivity.value) params.set('has_unseen_activity', 'true')

    const queryString = params.toString()
    const url = `${OpenAPI.BASE}/api/v3/cases/state-counts/${queryString ? `?${queryString}` : ''}`

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error('Failed to fetch state counts')
    }

    return response.json()
  }

  const fetchCounts = async () => {
    isCountsLoading.value = true
    try {
      const data = await fetchStateCounts()

      // Always store total counts
      totalNewCasesCount.value = data.total.NEW.count
      totalInProgressCasesCount.value = data.total.IN_PROGRESS.count
      totalCompletedCasesCount.value = data.total.COMPLETED.count
      totalRejectedCasesCount.value = data.total.REJECTED.count

      // Use filtered counts if filters are applied, otherwise use total counts
      hasActiveFilters.value = data.has_filters
      const counts = data.has_filters && data.filtered ? data.filtered : data.total

      newCasesCount.value = counts.NEW.count
      inProgressCasesCount.value = counts.IN_PROGRESS.count
      completedCasesCount.value = counts.COMPLETED.count
      rejectedCasesCount.value = counts.REJECTED.count

      // Update unseen activity indicators from total counts (always show total unseen status)
      hasUnseenActivityByState.value.NEW = data.total.NEW.has_unseen_activity
      hasUnseenActivityByState.value.IN_PROGRESS = data.total.IN_PROGRESS.has_unseen_activity
      hasUnseenActivityByState.value.COMPLETED = data.total.COMPLETED.has_unseen_activity
      hasUnseenActivityByState.value.REJECTED = data.total.REJECTED.has_unseen_activity
    } finally {
      isCountsLoading.value = false
    }
  }

  const changeState = (state: StateType) => {
    selectedState.value = state
    updateUrlParams()
    if (tableRef.value) {
      tableRef.value.resetAndRefresh()
    }
  }

  const clearFilters = () => {
    clerkFilter.value = ''
    lawFieldFilter.value = ''
    searchQuery.value = ''
    onlyUnseenActivity.value = false
    updateUrlParams()
    if (tableRef.value) {
      tableRef.value.resetAndRefresh()
    }
    fetchCounts()
  }

  const handleViewDetails = (event: MouseEvent, caseItem: CustomerCase) => {
    const url = `/app/cases/legacy/${caseItem.uuid}/`
    // Support ctrl+click, cmd+click (Mac), and middle-click to open in new tab
    if (event.ctrlKey || event.metaKey || event.button === 1) {
      window.open(url, '_blank')
    } else {
      window.location.href = url
    }
  }

  const handleCreateCase = () => {
    window.location.href = '/app/cases/create'
  }

  const markAsSeen = async (caseItem: CustomerCase) => {
    try {
      await CasesService.casesPartialUpdate({
        uuid: caseItem.uuid!,
        requestBody: {
          last_activity_seen_at: new Date().toISOString()
        }
      })
      if (tableRef.value) {
        tableRef.value.refreshData()
      }
      fetchCounts()
    } catch (error) {
      console.error('Error marking case as seen:', error)
    }
  }

  const markAsUnseen = async (caseItem: CustomerCase) => {
    try {
      const unseenTime = caseItem.last_activity_at
        ? new Date(new Date(caseItem.last_activity_at).getTime() - 60000).toISOString()
        : new Date(Date.now() - 60000).toISOString()
      await CasesService.casesPartialUpdate({
        uuid: caseItem.uuid!,
        requestBody: {
          last_activity_seen_at: unseenTime
        }
      })
      if (tableRef.value) {
        tableRef.value.refreshData()
      }
      fetchCounts()
    } catch (error) {
      console.error('Error marking case as unseen:', error)
    }
  }

  const updateClerk = async (caseItem: CustomerCase, clerkUuid: string | null) => {
    const previousClerkUuid = (caseItem as any).clerk?.uuid || null
    updatingClerkForCase.value = caseItem.uuid!
    try {
      await CasesService.casesPartialUpdate({
        uuid: caseItem.uuid!,
        requestBody: {
          clerk_id: clerkUuid || null
        } as any
      })
      // Update the local caseItem directly instead of refreshing the whole table
      if (clerkUuid) {
        const clerkUser = userStore.user?.org?.users?.find((u) => u.uuid === clerkUuid)
        if (clerkUser) {
          ;(caseItem as any).clerk = {
            uuid: clerkUser.uuid,
            first_name: clerkUser.first_name,
            last_name: clerkUser.last_name
          }
        }
      } else {
        ;(caseItem as any).clerk = null
      }

      if (!previousClerkUuid && clerkUuid) {
        trackEvent(MetricEventType.CASE_LIST_CLERK_ASSIGNED, {
          case_uuid: caseItem.uuid,
          clerk_uuid: clerkUuid
        })
      } else if (previousClerkUuid && !clerkUuid) {
        trackEvent(MetricEventType.CASE_LIST_CLERK_REMOVED, {
          case_uuid: caseItem.uuid,
          previous_clerk_uuid: previousClerkUuid
        })
      } else if (previousClerkUuid && clerkUuid && previousClerkUuid !== clerkUuid) {
        trackEvent(MetricEventType.CASE_LIST_CLERK_CHANGED, {
          case_uuid: caseItem.uuid,
          previous_clerk_uuid: previousClerkUuid,
          new_clerk_uuid: clerkUuid
        })
      }
    } catch (error) {
      console.error('Error updating clerk:', error)
    } finally {
      updatingClerkForCase.value = null
    }
  }

  const formatDate = (timestamp: string | undefined) => {
    if (!timestamp) return '-'
    return new Date(timestamp).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    if (diffInSeconds < 60) {
      return 'gerade eben'
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60)
      return `vor ${minutes} Minute${minutes !== 1 ? 'n' : ''}`
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600)
      return `vor ${hours} Stunde${hours !== 1 ? 'n' : ''}`
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400)
      return `vor ${days} Tag${days !== 1 ? 'en' : ''}`
    } else if (diffInSeconds < 2592000) {
      const weeks = Math.floor(diffInSeconds / 604800)
      return `vor ${weeks} Woche${weeks !== 1 ? 'n' : ''}`
    } else if (diffInSeconds < 31536000) {
      const months = Math.floor(diffInSeconds / 2592000)
      return `vor ${months} Monat${months !== 1 ? 'en' : ''}`
    } else {
      const years = Math.floor(diffInSeconds / 31536000)
      return `vor ${years} Jahr${years !== 1 ? 'en' : ''}`
    }
  }

  // Activity helpers
  const fetchActivities = async (caseUuid: string) => {
    if (activitiesCache.value[caseUuid]) {
      return activitiesCache.value[caseUuid]
    }

    loadingActivities.value[caseUuid] = true
    try {
      const response = await ActivitiesService.activitiesList({
        _case: caseUuid,
        limit: 1,
        ordering: '-created_at'
      })
      activitiesCache.value[caseUuid] = response.results || []
      return activitiesCache.value[caseUuid]
    } catch (error) {
      console.error('Error fetching activities:', error)
      return []
    } finally {
      loadingActivities.value[caseUuid] = false
    }
  }

  const openActivityPopover = (caseUuid: string) => {
    if (activityPopoverCloseTimeout.value[caseUuid]) {
      clearTimeout(activityPopoverCloseTimeout.value[caseUuid])
      activityPopoverCloseTimeout.value[caseUuid] = 0
    }
    activityPopoverOpen.value[caseUuid] = true
    fetchActivities(caseUuid)
  }

  const closeActivityPopover = (caseUuid: string) => {
    if (activityPopoverCloseTimeout.value[caseUuid]) {
      clearTimeout(activityPopoverCloseTimeout.value[caseUuid])
    }
    activityPopoverCloseTimeout.value[caseUuid] = window.setTimeout(() => {
      activityPopoverOpen.value[caseUuid] = false
    }, 100)
  }

  const getActivityDescription = (activity: TActivity) => {
    const activityType = activity.activity_type

    switch (activityType) {
      case 'CASE_CREATED':
        return t('activityCaseCreated')
      case 'DOCUMENT_SIGNED':
        return activity.document_title
          ? t('activityDocumentSignedWithTitle', { title: activity.document_title })
          : t('activityDocumentSigned')
      case 'QUESTIONNAIRE_CLOSED':
        return activity.questionnaire_title
          ? t('activityQuestionnaireClosedWithTitle', { title: activity.questionnaire_title })
          : t('activityQuestionnaireClosed')
      case 'EMAIL_RECEIVED':
        return activity.email_from
          ? t('activityEmailReceivedFrom', { from: activity.email_from })
          : t('activityEmailReceived')
      case 'EMAIL_SENT':
        return t('activityEmailSent')
      case 'APPOINTMENT_CREATED':
        return t('activityAppointmentCreated')
      default:
        return activityType
    }
  }

  const getUserInitials = (firstName: string | null, lastName: string | null) => {
    if (!firstName && !lastName) return 'J'
    const first = firstName?.[0] || ''
    const last = lastName?.[0] || ''
    return (first + last).toUpperCase()
  }

  const getUserDisplayName = (firstName: string | null, lastName: string | null) => {
    if (!firstName && !lastName) return t('systemUser')
    return `${firstName || ''} ${lastName || ''}`.trim()
  }

  // Document count helpers
  const getCompletedCount = (caseItem: CustomerCase) =>
    (caseItem as any).completed_documents_count ?? 0
  const getIncompletedCount = (caseItem: CustomerCase) =>
    (caseItem as any).incompleted_documents_count ?? 0
  const getTotalCount = (caseItem: CustomerCase) =>
    getCompletedCount(caseItem) + getIncompletedCount(caseItem)
  const hasAnyDocuments = (caseItem: CustomerCase) => getTotalCount(caseItem) > 0
  const hasReceivedAllDocuments = (caseItem: CustomerCase) =>
    hasAnyDocuments(caseItem) && getIncompletedCount(caseItem) === 0

  const selectLawField = (value: string) => {
    lawFieldFilter.value = value
    lawFieldDropdownOpen.value = false
    lawFieldDropdownSearch.value = ''
    if (value) {
      trackEvent(MetricEventType.CASE_LIST_LAW_FIELD_FILTER_USED, {
        law_field: value,
        current_state: selectedState.value
      })
    }
  }

  const selectClerk = (value: string) => {
    clerkFilter.value = value
    clerkDropdownOpen.value = false
    clerkDropdownSearch.value = ''
    if (value) {
      const isSameAsCurrentUser = value === userStore.user?.uuid
      trackEvent(MetricEventType.CASE_LIST_CLERK_FILTER_USED, {
        clerk_uuid: value,
        is_same_as_current_user: isSameAsCurrentUser,
        current_state: selectedState.value
      })
    }
  }

  const selectInlineClerk = async (caseItem: CustomerCase, clerkUuid: string | null) => {
    await updateClerk(caseItem, clerkUuid)
    inlineClerkSearch.value = ''
  }

  const clearInlineClerkSearch = (open: boolean) => {
    if (!open) inlineClerkSearch.value = ''
  }

  /**
   * Get the display name for a case client based on entity type.
   * Uses the same logic as LegacyCaseHeader.vue:
   * - NATURAL_PERSON: "F. LastName" (first initial + last name)
   * - LEGAL_PERSON: company_name (fallback to name)
   * - INSTITUTION: institution_name (fallback to name)
   */
  const getClientDisplayName = (caseItem: CustomerCase): string => {
    const client = caseItem.data?.client
    if (!client) return ''

    // Build the person name (first initial + last name)
    let personName = ''
    if (client.last_name) {
      if (client.first_name && client.first_name.length > 0) {
        const firstNameChar = client.first_name.charAt(0).toUpperCase()
        personName = `${firstNameChar}. ${client.last_name}`.trim()
      } else {
        personName = client.last_name
      }
    } else if (client.first_name) {
      personName = client.first_name
    }

    // Return based on entity type
    switch (client.entity_type) {
      case EntityTypeEnum.NATURAL_PERSON:
        return personName
      case EntityTypeEnum.LEGAL_PERSON:
        return client.company_name || personName || ''
      case EntityTypeEnum.INSTITUTION:
        return client.institution_name || personName || ''
      default:
        return personName || ''
    }
  }

  const openNoteDialog = (caseItem: CustomerCase, event: Event) => {
    event.stopPropagation()
    editingNoteForCase.value = caseItem.uuid!
    editNoteText.value = caseItem.note || ''
    originalNoteText.value = caseItem.note || ''
    noteDialogOpen.value = true
  }

  const saveNote = async () => {
    if (!editingNoteForCase.value) return

    savingNote.value = true
    try {
      await CasesService.casesPartialUpdate({
        uuid: editingNoteForCase.value,
        requestBody: {
          note: editNoteText.value || null
        } as any
      })

      const hadNoteOriginally = originalNoteText.value.trim().length > 0
      const hasNoteNow = editNoteText.value.trim().length > 0

      if (!hadNoteOriginally && hasNoteNow) {
        trackEvent(MetricEventType.CASE_LIST_NOTE_ADDED, {
          case_uuid: editingNoteForCase.value
        })
      } else if (hadNoteOriginally && hasNoteNow) {
        trackEvent(MetricEventType.CASE_LIST_NOTE_EDITED, {
          case_uuid: editingNoteForCase.value
        })
      }

      noteDialogOpen.value = false
      if (tableRef.value) {
        tableRef.value.updateItem(editingNoteForCase.value, { note: editNoteText.value || null })
      }
    } catch (error) {
      console.error('Error saving note:', error)
    } finally {
      savingNote.value = false
    }
  }

  const handleDelete = (caseItem: CustomerCase) => {
    caseToDelete.value = caseItem
    showDeleteModal.value = true
  }

  const handleDeleteCase = async () => {
    if (!caseToDelete.value) return

    try {
      await CasesService.casesDestroy({
        uuid: caseToDelete.value.uuid!
      })

      showDeleteModal.value = false
      if (tableRef.value) {
        tableRef.value.refreshData()
      }
      fetchCounts()
    } catch (error) {
      console.error('Error deleting case:', error)
    }
  }

  onMounted(() => {
    userStore.fetchUser()
    fetchCounts()
    initFeatureAnnouncement()
    // Set initial URL params if not already present
    if (!route.query.state) {
      updateUrlParams()
    }
  })

  onBeforeUnmount(() => {
    // Clean up all activity popover timeouts to prevent memory leaks
    Object.values(activityPopoverCloseTimeout.value).forEach((timeout) => {
      if (timeout) {
        clearTimeout(timeout)
      }
    })
  })
</script>

<template>
  <SpaCompatComponent>
    <div class="min-h-screen pb-10 pt-3">
      <div class="mx-auto space-y-4">
        <!-- Header -->
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1
              data-cy="legacy-cases-page-title"
              class="text-2xl font-semibold text-gray-900"
            >
              {{ t('requests') }}
            </h1>
            <p class="mt-1 text-sm text-gray-500">{{ t('manageClientRequests') }}</p>
          </div>
          <Button
            data-cy="btn-create-new-case"
            class="w-full gap-2 sm:w-auto"
            @click="handleCreateCase"
          >
            <Plus class="h-4 w-4" />
            {{ t('createNewRequest') }}
          </Button>
        </div>

        <!-- Feature Announcement Overlay -->
        <Teleport to="body">
          <Transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <div
              v-if="showFeatureAnnouncement"
              data-cy="feature-announcement-overlay"
              class="fixed inset-0 z-50"
            >
              <!-- Backdrop -->
              <div
                class="absolute inset-0 bg-black/20"
                @click="dismissFeatureAnnouncement"
              ></div>

              <!-- Modal -->
              <div
                data-cy="feature-announcement"
                class="absolute right-4 top-28 w-[calc(100%-2rem)] max-w-[280px] rounded-lg border border-gray-200 bg-white
                  p-4 shadow-xl sm:right-32 sm:top-32 sm:max-w-[300px] lg:right-48 xl:right-72"
              >
                <!-- Close button -->
                <button
                  data-cy="feature-announcement-close"
                  class="absolute right-3 top-3 rounded-full p-0.5 text-gray-400 transition-colors hover:bg-gray-100
                    hover:text-gray-600"
                  @click="dismissFeatureAnnouncement"
                >
                  <X class="h-4 w-4" />
                </button>

                <!-- Content -->
                <div class="pr-5">
                  <h3
                    data-cy="feature-announcement-title"
                    class="mb-2 text-base font-semibold leading-tight text-gray-900"
                  >
                    {{ t('announcementTitle') }}
                  </h3>
                  <p
                    data-cy="feature-announcement-description"
                    class="mb-4 text-sm leading-relaxed text-gray-600"
                  >
                    {{ t('announcementDescription') }}
                  </p>
                  <div class="flex justify-end">
                    <Button
                      data-cy="feature-announcement-dismiss"
                      size="sm"
                      @click="dismissFeatureAnnouncement"
                    >
                      {{ t('announcementDone') }}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </Teleport>

        <!-- State Cards -->
        <div
          data-cy="state-cards-container"
          class="grid grid-cols-2 gap-2 md:gap-3 lg:grid-cols-4 lg:gap-4"
        >
          <!-- New Cases Card -->
          <button
            data-cy="state-card-new"
            :class="[
              `flex cursor-pointer flex-col gap-1 rounded-lg border px-3 py-2 text-left transition-all sm:flex-row
              sm:items-center sm:justify-between sm:px-5 sm:py-3`,
              selectedState === STATES.NEW
                ? 'border-purple-600 bg-purple-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            ]"
            @click="changeState(STATES.NEW)"
          >
            <div class="flex items-center gap-2">
              <ClipboardPlus class="h-5 w-5 flex-shrink-0 text-gray-400" />
              <span class="text-sm text-gray-600">{{ t('newRequests') }}</span>
              <span
                v-if="hasUnseenActivityByState.NEW"
                data-cy="state-card-new-unseen-indicator"
                class="h-2 w-2 rounded-full bg-red-500"
              ></span>
            </div>
            <div class="text-right">
              <span
                v-if="isCountsLoading"
                class="inline-block h-5 w-12 animate-pulse rounded bg-gray-200 sm:w-16"
              ></span>
              <span
                v-else
                data-cy="state-card-new-count"
                class="font-semibold text-gray-900"
              >
                {{ newCasesCount }}
                <span class="hidden font-normal text-gray-400 sm:inline">
                  / {{ totalNewCasesCount }}
                </span>
              </span>
            </div>
          </button>

          <!-- In Progress Card -->
          <button
            data-cy="state-card-in-progress"
            :class="[
              `flex cursor-pointer flex-col gap-1 rounded-lg border px-3 py-2 text-left transition-all sm:flex-row
              sm:items-center sm:justify-between sm:px-5 sm:py-3`,
              selectedState === STATES.IN_PROGRESS
                ? 'border-purple-600 bg-purple-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            ]"
            @click="changeState(STATES.IN_PROGRESS)"
          >
            <div class="flex items-center gap-2">
              <ClipboardList class="h-5 w-5 flex-shrink-0 text-gray-400" />
              <span class="text-sm text-gray-600">{{ t('inProgress') }}</span>
              <span
                v-if="hasUnseenActivityByState.IN_PROGRESS"
                data-cy="state-card-in-progress-unseen-indicator"
                class="h-2 w-2 rounded-full bg-red-500"
              ></span>
            </div>
            <div class="text-right">
              <span
                v-if="isCountsLoading"
                class="inline-block h-5 w-12 animate-pulse rounded bg-gray-200 sm:w-16"
              ></span>
              <span
                v-else
                data-cy="state-card-in-progress-count"
                class="font-semibold text-gray-900"
              >
                {{ inProgressCasesCount }}
                <span class="hidden font-normal text-gray-400 sm:inline">
                  / {{ totalInProgressCasesCount }}
                </span>
              </span>
            </div>
          </button>

          <!-- Completed Card -->
          <button
            data-cy="state-card-completed"
            :class="[
              `flex cursor-pointer flex-col gap-1 rounded-lg border px-3 py-2 text-left transition-all sm:flex-row
              sm:items-center sm:justify-between sm:px-5 sm:py-3`,
              selectedState === STATES.COMPLETED
                ? 'border-purple-600 bg-purple-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            ]"
            @click="changeState(STATES.COMPLETED)"
          >
            <div class="flex items-center gap-2">
              <ClipboardCheck class="h-5 w-5 flex-shrink-0 text-gray-400" />
              <span class="text-sm text-gray-600">{{ t('completed') }}</span>
              <span
                v-if="hasUnseenActivityByState.COMPLETED"
                data-cy="state-card-completed-unseen-indicator"
                class="h-2 w-2 rounded-full bg-red-500"
              ></span>
            </div>
            <div class="text-right">
              <span
                v-if="isCountsLoading"
                class="inline-block h-5 w-12 animate-pulse rounded bg-gray-200 sm:w-16"
              ></span>
              <span
                v-else
                data-cy="state-card-completed-count"
                class="font-semibold text-gray-900"
              >
                {{ completedCasesCount }}
                <span class="hidden font-normal text-gray-400 sm:inline">
                  / {{ totalCompletedCasesCount }}
                </span>
              </span>
            </div>
          </button>

          <!-- Rejected Card -->
          <button
            data-cy="state-card-rejected"
            :class="[
              `flex cursor-pointer flex-col gap-1 rounded-lg border px-3 py-2 text-left transition-all sm:flex-row
              sm:items-center sm:justify-between sm:px-5 sm:py-3`,
              selectedState === STATES.REJECTED
                ? 'border-purple-600 bg-purple-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            ]"
            @click="changeState(STATES.REJECTED)"
          >
            <div class="flex items-center gap-2">
              <ClipboardX class="h-5 w-5 flex-shrink-0 text-gray-400" />
              <span class="text-sm text-gray-600">{{ t('rejected') }}</span>
              <span
                v-if="hasUnseenActivityByState.REJECTED"
                data-cy="state-card-rejected-unseen-indicator"
                class="h-2 w-2 rounded-full bg-red-500"
              ></span>
            </div>
            <div class="text-right">
              <span
                v-if="isCountsLoading"
                class="inline-block h-5 w-12 animate-pulse rounded bg-gray-200 sm:w-16"
              ></span>
              <span
                v-else
                data-cy="state-card-rejected-count"
                class="font-semibold text-gray-900"
              >
                {{ rejectedCasesCount }}
                <span class="hidden font-normal text-gray-400 sm:inline">
                  / {{ totalRejectedCasesCount }}
                </span>
              </span>
            </div>
          </button>
        </div>

        <!-- Table Card -->
        <div
          data-cy="cases-table-container"
          class="rounded-xl bg-white p-3 shadow-sm ring-1 ring-gray-900/5 sm:p-4"
        >
          <!-- Filters Row inside card -->
          <div
            data-cy="filters-row"
            class="mb-2 flex flex-col gap-3 md:flex-row md:items-center md:gap-4"
          >
            <!-- Search input -->
            <div class="relative w-full md:max-w-[320px] md:flex-1">
              <Search class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                v-model="searchQuery"
                data-cy="search-input"
                type="text"
                :placeholder="t('searchPlaceholder')"
                class="pl-9"
                autocomplete="off"
                name="case-filter-query"
              />
            </div>

            <!-- Filter controls wrapper -->
            <div class="flex flex-wrap items-center gap-2 md:gap-4">
              <!-- Law field filter dropdown -->
              <Popover v-model:open="lawFieldDropdownOpen">
                <PopoverTrigger asChild>
                  <button
                    data-cy="filter-law-field-trigger"
                    class="inline-flex max-w-[200px] items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-2
                      text-sm hover:bg-gray-50"
                    :title="lawFieldFilter ? selectedLawFieldLabel : t('all')"
                  >
                    <span class="shrink-0 text-gray-500">{{ t('legalArea') }}:</span>
                    <span
                      data-cy="filter-law-field-value"
                      class="truncate font-medium text-gray-900"
                    >
                      {{ lawFieldFilter ? selectedLawFieldLabel : t('all') }}
                    </span>
                    <ChevronDown class="h-4 w-4 shrink-0 text-gray-400" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  data-cy="filter-law-field-popover"
                  class="w-[240px] p-0"
                  align="start"
                >
                  <div class="p-2">
                    <div class="relative">
                      <Search
                        class="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                      />
                      <Input
                        v-model="lawFieldDropdownSearch"
                        data-cy="filter-law-field-search"
                        type="text"
                        :placeholder="t('search')"
                        class="h-8 pl-8 text-sm"
                        autocomplete="off"
                      />
                    </div>
                  </div>
                  <div class="max-h-[200px] overflow-y-auto border-t border-gray-100">
                    <button
                      v-for="option in filteredLawFieldOptions"
                      :key="option.value"
                      :data-cy="`filter-law-field-option-${option.value || 'all'}`"
                      class="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                      :class="{ 'bg-gray-50': lawFieldFilter === option.value }"
                      :title="option.title"
                      @click="selectLawField(option.value)"
                    >
                      <span class="truncate">{{ option.title }}</span>
                      <Check
                        v-if="lawFieldFilter === option.value"
                        class="h-4 w-4 shrink-0 text-purple-600"
                      />
                    </button>
                  </div>
                </PopoverContent>
              </Popover>

              <!-- Clerk filter dropdown -->
              <Popover v-model:open="clerkDropdownOpen">
                <PopoverTrigger asChild>
                  <button
                    data-cy="filter-clerk-trigger"
                    class="inline-flex max-w-[200px] items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-2
                      text-sm hover:bg-gray-50"
                    :title="clerkFilter ? selectedClerkLabel : t('all')"
                  >
                    <span class="shrink-0 text-gray-500">{{ t('clerkLabel') }}:</span>
                    <span
                      data-cy="filter-clerk-value"
                      class="truncate font-medium text-gray-900"
                    >
                      {{ clerkFilter ? selectedClerkLabel : t('all') }}
                    </span>
                    <ChevronDown class="h-4 w-4 shrink-0 text-gray-400" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  data-cy="filter-clerk-popover"
                  class="w-[240px] p-0"
                  align="start"
                >
                  <div class="p-2">
                    <div class="relative">
                      <Search
                        class="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                      />
                      <Input
                        v-model="clerkDropdownSearch"
                        data-cy="filter-clerk-search"
                        type="text"
                        :placeholder="t('search')"
                        class="h-8 pl-8 text-sm"
                        autocomplete="off"
                      />
                    </div>
                  </div>
                  <div class="max-h-[200px] overflow-y-auto border-t border-gray-100">
                    <button
                      v-for="option in filteredClerkOptions"
                      :key="option.value"
                      :data-cy="`filter-clerk-option-${option.value || 'all'}`"
                      class="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                      :class="{ 'bg-gray-50': clerkFilter === option.value }"
                      :title="option.title"
                      @click="selectClerk(option.value)"
                    >
                      <span class="truncate">{{ option.title }}</span>
                      <Check
                        v-if="clerkFilter === option.value"
                        class="h-4 w-4 shrink-0 text-purple-600"
                      />
                    </button>
                  </div>
                </PopoverContent>
              </Popover>

              <!-- Unseen activity checkbox -->
              <CheckboxField
                v-model="onlyUnseenActivity"
                data-cy="filter-unseen-activity"
                name="onlyUnseenActivity"
                :label="t('onlyUnseenActivity')"
              />

              <!-- Spacer (hidden on mobile) -->
              <div class="hidden flex-1 md:block"></div>

              <!-- Clear all link -->
              <button
                data-cy="btn-clear-filters"
                class="whitespace-nowrap text-sm text-gray-600 underline hover:text-gray-900"
                :class="{ invisible: !hasActiveFilters }"
                @click="clearFilters"
              >
                {{ t('clearAll') }}
              </button>
            </div>
          </div>

          <PaginatedTableView
            ref="tableRef"
            :fetchData="fetchCases"
            :show-column-dividers="false"
            pagination-nav-data-cy="pagination-nav"
            pagination-prev-data-cy="pagination-prev"
            pagination-next-data-cy="pagination-next"
            pagination-input-data-cy="pagination-input"
            pagination-results-count-data-cy="pagination-results-count"
          >
            <template #table-head>
              <TableCell class="w-[40px] min-w-[40px] py-3"></TableCell>
              <SortableTableHeader
                field="client_last_name"
                :current-sort="sortBy"
                class="w-[180px] min-w-[120px]"
                @sort="handleSort"
              >
                {{ t('name') }}
              </SortableTableHeader>
              <SortableTableHeader
                field="created_at"
                :current-sort="sortBy"
                class="w-[140px] min-w-[100px]"
                @sort="handleSort"
              >
                {{ t('received') }}
              </SortableTableHeader>
              <SortableTableHeader
                field="last_activity_at"
                :current-sort="sortBy"
                class="w-[140px] min-w-[100px]"
                @sort="handleSort"
              >
                {{ t('lastActivity') }}
              </SortableTableHeader>
              <TableCell class="w-[160px] min-w-[120px] py-3">
                <div>{{ t('lawField') }} /</div>
                <div>{{ t('lawTopic') }}</div>
              </TableCell>
              <SortableTableHeader
                field="clerk__first_name"
                :current-sort="sortBy"
                class="w-[200px] min-w-[160px]"
                @sort="handleSort"
              >
                {{ t('clerk') }}
              </SortableTableHeader>
              <TableCell class="w-[100px] min-w-[80px] py-3">{{ t('documents') }}</TableCell>
              <TableCell class="w-[200px] min-w-[150px] py-3">{{ t('note') }}</TableCell>
              <TableCell class="w-[50px] min-w-[50px] py-3"></TableCell>
            </template>

            <template #table-row="{ items }">
              <TableRow
                v-for="(caseItem, index) in items"
                :key="caseItem.uuid"
                :data-cy="`case-row-${caseItem.uuid}`"
                class="group animate-fade-in cursor-pointer border-b border-gray-100 hover:bg-gray-50/50"
                :style="{ animationDelay: `${index * 30}ms` }"
                @click="handleViewDetails($event, caseItem)"
                @auxclick="handleViewDetails($event, caseItem)"
              >
                <!-- Unseen indicator -->
                <TableCell
                  data-cy="cell-unseen-indicator"
                  class="py-2.5"
                >
                  <span
                    v-if="caseItem.has_unseen_activity"
                    data-cy="indicator-unseen"
                    class="flex h-2.5 w-2.5 rounded-full bg-red-500"
                  ></span>
                </TableCell>

                <!-- Name (clickable, purple on hover) -->
                <TableCell
                  data-cy="cell-client-name"
                  class="max-w-[180px] py-2.5"
                >
                  <button
                    data-cy="btn-view-case"
                    class="block max-w-full cursor-pointer truncate font-medium text-gray-900 hover:text-purple-600"
                    :title="getClientDisplayName(caseItem) || '-'"
                    @click.stop="handleViewDetails($event, caseItem)"
                    @auxclick.stop="handleViewDetails($event, caseItem)"
                  >
                    {{ getClientDisplayName(caseItem) || '-' }}
                  </button>
                </TableCell>

                <!-- Received Date -->
                <TableCell
                  data-cy="cell-received-date"
                  class="whitespace-nowrap py-2.5 text-sm text-gray-700"
                >
                  {{ formatDate(caseItem.created_at) }}
                </TableCell>

                <!-- Last Activity -->
                <TableCell
                  data-cy="cell-last-activity"
                  class="py-2.5 text-sm"
                  @click.stop
                >
                  <Popover
                    v-if="caseItem.last_activity_at"
                    :open="activityPopoverOpen[caseItem.uuid]"
                    @update:open="(open) => (activityPopoverOpen[caseItem.uuid] = open)"
                  >
                    <PopoverTrigger as-child>
                      <div
                        class="max-w-[140px] cursor-pointer"
                        @mouseenter="openActivityPopover(caseItem.uuid)"
                        @mouseleave="closeActivityPopover(caseItem.uuid)"
                      >
                        <div
                          class="text-sm"
                          style="color: #1f2937"
                        >
                          {{ formatRelativeTime(caseItem.last_activity_at) }}:
                        </div>
                        <div
                          class="truncate text-sm"
                          style="color: #6b7280"
                        >
                          <template v-if="loadingActivities[caseItem.uuid]">
                            <Loader2 class="inline h-3 w-3 animate-spin" />
                          </template>
                          <template v-else-if="activitiesCache[caseItem.uuid]?.[0]">
                            {{ getActivityDescription(activitiesCache[caseItem.uuid][0]) }}
                          </template>
                          <template v-else>&nbsp;</template>
                        </div>
                      </div>
                    </PopoverTrigger>
                    <PopoverContent
                      class="flex w-[400px] cursor-pointer flex-col items-start rounded-xl border border-gray-200 bg-white p-0"
                      align="start"
                      :side-offset="4"
                      style="
                        box-shadow:
                          0 8px 12px 0 rgba(0, 0, 0, 0.08),
                          0 4px 8px 0 rgba(0, 0, 0, 0.08);
                      "
                      @mouseenter="openActivityPopover(caseItem.uuid)"
                      @mouseleave="closeActivityPopover(caseItem.uuid)"
                      @click="handleViewDetails($event, caseItem)"
                    >
                      <div class="w-full border-b border-gray-200 px-3 py-3">
                        <h3 class="text-sm font-semibold leading-5 text-gray-800">
                          {{ t('latestActivity') }}
                        </h3>
                      </div>

                      <div
                        v-if="loadingActivities[caseItem.uuid]"
                        class="flex w-full items-center justify-center rounded-b-xl py-8"
                      >
                        <Loader2 class="h-5 w-5 animate-spin text-gray-400" />
                      </div>

                      <div
                        v-else-if="activitiesCache[caseItem.uuid]?.[0]"
                        class="flex w-full items-center gap-3 rounded-b-xl bg-white p-3"
                      >
                        <div class="flex-shrink-0">
                          <div class="h-2 w-2 rounded-full bg-red-500"></div>
                        </div>

                        <div class="flex min-w-0 flex-1 flex-col gap-2">
                          <div class="flex items-center justify-between gap-3">
                            <div class="flex items-center gap-2">
                              <div
                                class="flex h-6 min-h-[24px] w-6 min-w-[24px] flex-shrink-0 items-center justify-center rounded-full p-1
                                  text-xs font-medium text-white"
                                :class="
                                  !activitiesCache[caseItem.uuid][0].clerk_first_name &&
                                  !activitiesCache[caseItem.uuid][0].clerk_last_name
                                    ? 'bg-[#9333EA]'
                                    : 'bg-purple-500'
                                "
                              >
                                {{
                                  getUserInitials(
                                    activitiesCache[caseItem.uuid][0].clerk_first_name,
                                    activitiesCache[caseItem.uuid][0].clerk_last_name
                                  )
                                }}
                              </div>
                              <p class="text-sm font-medium leading-5 text-gray-800">
                                {{
                                  getUserDisplayName(
                                    activitiesCache[caseItem.uuid][0].clerk_first_name,
                                    activitiesCache[caseItem.uuid][0].clerk_last_name
                                  )
                                }}
                              </p>
                            </div>
                            <p class="flex-shrink-0 text-sm font-normal leading-5 text-gray-500">
                              {{ formatRelativeTime(activitiesCache[caseItem.uuid][0].created_at) }}
                            </p>
                          </div>

                          <p class="line-clamp-2 text-sm font-normal leading-5 text-gray-800">
                            {{ getActivityDescription(activitiesCache[caseItem.uuid][0]) }}
                          </p>
                        </div>
                      </div>

                      <div
                        v-else
                        class="w-full rounded-b-xl py-8 text-center text-sm text-gray-500"
                      >
                        {{ t('noActivitiesFound') }}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <div
                    v-else
                    class="text-sm text-gray-700"
                  >
                    -
                  </div>
                </TableCell>

                <!-- Law Field / Topic -->
                <TableCell
                  data-cy="cell-law-field"
                  class="max-w-[160px] py-2.5 text-sm"
                >
                  <div
                    data-cy="law-field-name"
                    class="truncate text-gray-800"
                  >
                    {{ caseItem.data?.law?.field || '-' }}
                  </div>
                  <div
                    data-cy="law-topic-name"
                    class="truncate text-gray-800"
                  >
                    {{ caseItem.data?.law?.topic || '\u00A0' }}
                  </div>
                </TableCell>

                <!-- Clerk -->
                <TableCell
                  data-cy="cell-clerk"
                  class="w-[200px] max-w-[200px] py-2.5"
                  @click.stop
                >
                  <!-- Loading state -->
                  <div
                    v-if="updatingClerkForCase === caseItem.uuid"
                    data-cy="clerk-loading"
                    class="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-2.5 py-1"
                  >
                    <Loader2 class="h-4 w-4 animate-spin text-purple-500" />
                    <span class="text-sm text-gray-400">{{ t('loading') }}</span>
                  </div>
                  <!-- Clerk assigned state -->
                  <div
                    v-else-if="caseItem.clerk"
                    data-cy="clerk-badge"
                    :title="`${caseItem.clerk.first_name} ${caseItem.clerk.last_name}`"
                    class="inline-flex max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-white py-1 pl-1
                      pr-1.5"
                  >
                    <div
                      class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-purple-500 text-[10px] font-medium
                        text-white"
                    >
                      {{
                        (caseItem.clerk.first_name?.charAt(0) || '') +
                        (caseItem.clerk.last_name?.charAt(0) || '')
                      }}
                    </div>
                    <span class="truncate text-sm text-gray-900">
                      {{ caseItem.clerk.first_name }} {{ caseItem.clerk.last_name }}
                    </span>
                    <button
                      data-cy="btn-remove-clerk"
                      class="shrink-0 rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      @click="updateClerk(caseItem, null)"
                    >
                      <X class="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <!-- No clerk - Add clerk button with popover -->
                  <Popover
                    v-else
                    @update:open="clearInlineClerkSearch"
                  >
                    <PopoverTrigger asChild>
                      <button
                        data-cy="btn-add-clerk"
                        class="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-sm
                          text-gray-500 hover:bg-gray-50"
                      >
                        {{ t('addClerk') }}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      data-cy="inline-clerk-popover"
                      class="w-[200px] p-0"
                      align="start"
                    >
                      <div class="p-2">
                        <div class="relative">
                          <Search
                            class="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                          />
                          <Input
                            v-model="inlineClerkSearch"
                            data-cy="inline-clerk-search"
                            type="text"
                            :placeholder="t('search')"
                            class="h-8 pl-8 text-sm"
                            autocomplete="off"
                          />
                        </div>
                      </div>
                      <div class="max-h-[200px] overflow-y-auto border-t border-gray-100">
                        <button
                          v-for="option in filteredInlineClerkOptions"
                          :key="option.value"
                          :data-cy="`inline-clerk-option-${option.value}`"
                          class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50"
                          @click="selectInlineClerk(caseItem, option.value || null)"
                        >
                          <div
                            class="flex h-6 w-6 items-center justify-center rounded-full bg-purple-500 text-[10px] font-medium
                              text-white"
                          >
                            {{
                              option.title
                                .split(' ')
                                .map((n) => n.charAt(0))
                                .join('')
                                .slice(0, 2)
                            }}
                          </div>
                          <span>{{ option.title }}</span>
                        </button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </TableCell>

                <!-- Documents -->
                <TableCell
                  data-cy="cell-documents"
                  class="py-2.5"
                  @click.stop
                >
                  <Popover v-if="hasAnyDocuments(caseItem)">
                    <PopoverTrigger asChild>
                      <button
                        data-cy="btn-documents"
                        class="inline-flex cursor-pointer items-center rounded px-2 py-1 text-sm font-medium"
                        :class="
                          hasReceivedAllDocuments(caseItem)
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-gray-100 text-gray-700'
                        "
                      >
                        <FileText class="mr-1.5 h-3.5 w-3.5" />
                        {{ getCompletedCount(caseItem) }}/{{ getTotalCount(caseItem) }}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      data-cy="documents-popover"
                      class="w-auto p-0"
                    >
                      <div class="flex items-center gap-3 p-2">
                        <div
                          data-cy="documents-status-badge"
                          class="inline-flex items-center gap-2 rounded px-3 py-1 text-sm font-medium ring-1"
                          :class="
                            hasReceivedAllDocuments(caseItem)
                              ? 'bg-gray-50 text-gray-700 ring-gray-200'
                              : 'bg-amber-50 text-amber-800 ring-amber-300'
                          "
                        >
                          <File
                            v-if="hasReceivedAllDocuments(caseItem)"
                            class="h-4 w-4"
                          />
                          <Hourglass
                            v-else
                            class="h-4 w-4"
                          />
                          <span v-if="hasReceivedAllDocuments(caseItem)">
                            {{ t('nDocuments', getTotalCount(caseItem)) }}
                          </span>
                          <span v-else>
                            {{ t('nRequestedForms', getIncompletedCount(caseItem)) }}
                          </span>
                        </div>
                        <PopoverClose as-child>
                          <button class="text-gray-400 hover:text-gray-600">
                            <X class="h-5 w-5" />
                          </button>
                        </PopoverClose>
                      </div>
                      <button
                        data-cy="btn-view-all-documents"
                        class="w-full px-2 pb-2 text-left text-sm text-purple-600 underline hover:text-purple-700"
                        @click="handleViewDetails($event, caseItem)"
                        @auxclick="handleViewDetails($event, caseItem)"
                      >
                        {{ t('viewAllDocuments') }}
                      </button>
                    </PopoverContent>
                  </Popover>
                  <span
                    v-else
                    data-cy="no-documents"
                    class="text-sm text-gray-800"
                  >
                    -
                  </span>
                </TableCell>

                <!-- Note -->
                <TableCell
                  data-cy="cell-note"
                  class="w-[200px] max-w-[200px] py-2.5"
                  @click.stop
                >
                  <div
                    v-if="caseItem.note"
                    data-cy="note-text"
                    :title="caseItem.note"
                    class="line-clamp-2 cursor-pointer break-words text-sm text-gray-600 hover:text-gray-900 hover:underline"
                    @click="openNoteDialog(caseItem, $event)"
                  >
                    {{ caseItem.note }}
                  </div>
                  <button
                    v-else
                    data-cy="btn-add-note"
                    class="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-sm
                      text-gray-500 hover:bg-gray-50"
                    @click="openNoteDialog(caseItem, $event)"
                  >
                    {{ t('addNote') }}
                  </button>
                </TableCell>

                <!-- Actions -->
                <TableCell
                  data-cy="cell-actions"
                  class="w-[50px] py-2.5"
                >
                  <div class="flex items-center justify-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          data-cy="btn-actions-menu"
                          variant="ghost"
                          size="icon"
                          class="h-7 w-7"
                          @click.stop
                        >
                          <MoreHorizontal class="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        data-cy="actions-dropdown"
                        align="end"
                      >
                        <DropdownMenuItem
                          v-if="caseItem.has_unseen_activity"
                          data-cy="action-mark-as-read"
                          @click.stop="markAsSeen(caseItem)"
                        >
                          <Eye class="mr-2 h-4 w-4" />
                          {{ t('markAsRead') }}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          v-else
                          data-cy="action-mark-as-unread"
                          @click.stop="markAsUnseen(caseItem)"
                        >
                          <EyeOff class="mr-2 h-4 w-4" />
                          {{ t('markAsUnread') }}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          data-cy="action-delete"
                          @click.stop="handleDelete(caseItem)"
                        >
                          <Trash2 class="mr-2 h-4 w-4" />
                          {{ t('deleteCase') }}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            </template>

            <!-- Custom empty state -->
            <template #empty>
              <div
                data-cy="empty-state"
                class="mx-auto max-w-md text-center"
              >
                <div
                  class="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100"
                >
                  <Search class="h-6 w-6 text-gray-400" />
                </div>
                <p
                  data-cy="empty-state-title"
                  class="text-base font-medium text-gray-900"
                >
                  {{ t('noRequestsFound') }}
                </p>
                <p
                  data-cy="empty-state-message"
                  class="mt-1 text-sm text-gray-500"
                >
                  <template v-if="hasActiveFilters">
                    {{ t('noResultsWithFilters') }}
                  </template>
                  <template v-else-if="hasNoRequestsAtAll">
                    {{ t('noRequestsYet') }}
                  </template>
                  <template v-else>
                    {{ t('noRequestsInStatus').replace('{status}', stateLabels[selectedState]) }}
                  </template>
                </p>

                <!-- Show other states with results (only if there are some requests somewhere) -->
                <div
                  v-if="otherStatesWithResults.length > 0 && !hasNoRequestsAtAll"
                  data-cy="other-states-section"
                  class="mt-5 border-t border-gray-100 pt-5"
                >
                  <p class="mb-3 text-sm text-gray-500">{{ t('requestsInOtherStatus') }}</p>
                  <div class="flex flex-wrap justify-center gap-2">
                    <button
                      v-for="item in otherStatesWithResults"
                      :key="item.state"
                      :data-cy="`btn-switch-to-${item.state.toLowerCase()}`"
                      class="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm
                        text-gray-700 transition-colors hover:border-purple-300 hover:bg-purple-50"
                      @click="changeState(item.state)"
                    >
                      {{ item.label }}
                      <span class="font-medium text-gray-900">{{ item.count }}</span>
                    </button>
                  </div>
                </div>

                <!-- Clear filters button if filters are active -->
                <div
                  v-if="hasActiveFilters"
                  class="mt-4"
                >
                  <button
                    data-cy="btn-reset-filters"
                    class="text-sm text-purple-600 hover:text-purple-700 hover:underline"
                    @click="clearFilters"
                  >
                    {{ t('resetFilters') }}
                  </button>
                </div>
              </div>
            </template>
          </PaginatedTableView>
        </div>
      </div>
    </div>

    <!-- Note Edit Dialog -->
    <Dialog v-model:open="noteDialogOpen">
      <DialogContent
        data-cy="note-dialog"
        class="max-w-lg"
      >
        <DialogHeader>
          <DialogTitle data-cy="note-dialog-title">{{ t('editNote') }}</DialogTitle>
        </DialogHeader>
        <div class="space-y-4">
          <Textarea
            v-model="editNoteText"
            data-cy="note-textarea"
            :placeholder="t('enterNote')"
            class="min-h-[120px] resize-none"
            :disabled="savingNote"
          />
          <div class="flex justify-end gap-2">
            <Button
              data-cy="btn-note-cancel"
              variant="outline"
              :disabled="savingNote"
              @click="noteDialogOpen = false"
            >
              {{ t('cancel') }}
            </Button>
            <Button
              data-cy="btn-note-save"
              :disabled="savingNote"
              @click="saveNote"
            >
              <Loader2
                v-if="savingNote"
                class="mr-2 h-4 w-4 animate-spin"
              />
              {{ t('save') }}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <AlertDialog v-model:open="showDeleteModal">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{{ t('deleteConfirmTitle') }}</AlertDialogTitle>
          <AlertDialogDescription>
            {{ t('deleteConfirmDescription') }}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel @click="showDeleteModal = false">
            {{ t('deleteConfirmCancel') }}
          </AlertDialogCancel>
          <AlertDialogAction
            class="bg-red-600 hover:bg-red-700"
            @click="handleDeleteCase"
          >
            {{ t('deleteConfirmDelete') }}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </SpaCompatComponent>
</template>
